import MuiChip from '@mui/material/Chip';
import is from '@sindresorhus/is';

import { HardSkillIcon } from './icons/HardSkillIcon';
import { LanguageSkillIcon } from './icons/LanguageSkillIcon';
import { SoftSkillIcon } from './icons/SoftSkillIcon';
import { getSkillTitle } from '../../shared/source/data-contract/skill';

import type { Skill, SkillType } from '../../shared/source/data-contract/skill';
import type { LanguageCode } from '../../shared/source/internationalization';
import type { SxProps } from '@mui/material';
import type { FunctionComponent, JSXElementConstructor, MouseEventHandler, ReactElement } from 'react';

type SkillIconVariant = 'job-architecture' | 'job-profile';

type SkillChipProperties = {
    readonly languageCode?: LanguageCode;
    readonly onClick?: MouseEventHandler;
    readonly skill: Skill;
    readonly variant?: SkillIconVariant;
};

const styles: SxProps = {
    backgroundColor: '#F1F1F0',
    border: '1px solid',
    borderColor: '#E7E7E7',
    borderRadius: '8px',
    height: '36px',
    padding: '8px 12px',
};

const jobProfileStyles: SxProps = {
    backgroundColor: '#F1F1F0',
    borderRadius: '0.5rem',
    boxShadow:
        '1px 1px 1px 0px #FBFBFF inset, -0.5px -0.5px 1px 0px rgba(0, 0, 0, 0.07) inset, 0.22px 0.22px 0.31px -0.5px rgba(0, 0, 0, 0.13), 0.3px 0.3px 0.86px -0.75px rgba(0, 0, 0, 0.13), 1.33px 1.33px 1.88px -1.13px rgba(0, 0, 0, 0.11), 2.95px 2.95px 4.18px -1.5px rgba(0, 0, 0, 0.10), 5px 5px 10.6px -1.88px rgba(0, 0, 0, 0.03), -0.32px -0.32px 0px 0px rgba(0, 0, 0, 0.05)',
    color: '#040F16',
    flexShrink: 2,
    fontFamily: 'FT Regola Neue',
    fontSize: '1rem',
    fontStyle: 'normal',
    fontWeight: 500,
    height: '2.25rem',
    letterSpacing: '-0.01rem',
    minWidth: '4rem',
    overflow: 'ellipsis',
    padding: '0.5rem 0.75rem',
};

const skillChipIcons: Record<SkillType, JSXElementConstructor<{ readonly variant: SkillIconVariant }>> = {
    hard: HardSkillIcon,
    language: LanguageSkillIcon,
    soft: SoftSkillIcon,
};

const getSkillChipIcon = (type: SkillType, variant: SkillIconVariant): ReactElement<any> => {
    const Icon = skillChipIcons[type];

    return <Icon variant={variant} />;
};

export const SkillChip: FunctionComponent<SkillChipProperties> = (properties) => {
    const { languageCode, onClick, skill, variant = 'job-architecture' } = properties;
    const clickable = is.function(onClick) === true;
    const { skillType } = skill;

    const icon = getSkillChipIcon(skillType, variant);
    const title = getSkillTitle(skill, languageCode);

    return (
        <MuiChip
            clickable={clickable}
            icon={icon}
            label={title}
            sx={variant === 'job-profile' ? jobProfileStyles : styles}
            title={title}
            onClick={onClick}
        />
    );
};
